import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Divider,
} from '@mui/material';
import { Settings as SettingsIcon, ViewList as ViewListIcon, Search as SearchIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../../services/api';

// Componente TabPanel para renderizar o conteúdo de cada aba
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Settings = () => {
  const [settings, setSettings] = useState({
    jira_api_url: '',
    jira_api_token: '',
    jira_projects: '',
    jira_username: '',
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentTab, setCurrentTab] = useState(0);
  const [testingConnection, setTestingConnection] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [issueNumber, setIssueNumber] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    checkAdminStatus();
    loadSettings();
    loadProjects();
  }, []);

  useEffect(() => {
    if (settings.jira_projects) {
      setSelectedProjects(settings.jira_projects.split(',').map(p => p.trim()));
    }
  }, [settings.jira_projects]);

  const checkAdminStatus = async () => {
    try {
      const response = await api.get('/users/me');
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao verificar permissões de administrador' });
    }
  };

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings({
        jira_api_url: response.data.jira_api_url || '',
        jira_api_token: response.data.jira_api_token || '',
        jira_projects: response.data.jira_projects || '',
        jira_username: response.data.jira_username || ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao carregar configurações' });
    }
  };

  const handleSave = async () => {
    try {
      await api.post('/settings', settings);
      setMessage({ type: 'success', text: 'Configurações salvas com sucesso!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erro ao salvar configurações' });
    }
  };

  const testConnection = async () => {
    try {
      setTestingConnection(true);
      setMessage({ type: '', text: '' });
      
      const response = await api.get('/settings/jira/test-connection');
      
      setMessage({ 
        type: 'success', 
        text: 'Conexão com o JIRA estabelecida com sucesso!' 
      });

      loadProjects();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao testar conexão com o JIRA' 
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const loadProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get('/settings/jira/projects');
      setAvailableProjects(response.data);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectToggle = (projectKey) => {
    const newSelected = selectedProjects.includes(projectKey)
      ? selectedProjects.filter(key => key !== projectKey)
      : [...selectedProjects, projectKey];
    
    setSelectedProjects(newSelected);
    setSettings({
      ...settings,
      jira_projects: newSelected.join(',')
    });
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const searchIssue = async () => {
    if (!issueNumber.trim()) {
      setMessage({ type: 'error', text: 'Digite um número de ficha para buscar' });
      return;
    }

    try {
      setSearching(true);
      setMessage({ type: '', text: '' });
      
      const response = await api.get(`/settings/jira/search-issue/${issueNumber}`);
      setSearchResults(response.data);
      
      if (response.data.length === 0) {
        setMessage({ type: 'info', text: 'Nenhuma issue encontrada com este número' });
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Erro ao buscar issue no JIRA' 
      });
    } finally {
      setSearching(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Você não tem permissão para acessar esta página.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Configurações do Sistema
          </Typography>
          
          {message.text && (
            <Alert severity={message.type} sx={{ mb: 2 }}>
              {message.text}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="configurações">
              <Tab 
                icon={<SettingsIcon />} 
                label="API do JIRA" 
                id="settings-tab-0"
                aria-controls="settings-tabpanel-0"
              />
              <Tab 
                icon={<ViewListIcon />} 
                label="Projetos" 
                id="settings-tab-1"
                aria-controls="settings-tabpanel-1"
              />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Configurações da API
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL da API do JIRA"
                  value={settings.jira_api_url}
                  onChange={(e) => setSettings({ ...settings, jira_api_url: e.target.value })}
                  helperText="Ex: https://seudominio.atlassian.net"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Token da API do JIRA"
                  type="password"
                  value={settings.jira_api_token}
                  onChange={(e) => setSettings({ ...settings, jira_api_token: e.target.value })}
                  helperText="Token de acesso à API do JIRA"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome de usuário do JIRA"
                  value={settings.jira_username}
                  onChange={(e) => setSettings({ ...settings, jira_username: e.target.value })}
                  helperText="Email ou nome de usuário da sua conta do JIRA"
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={testConnection}
                    disabled={testingConnection || !settings.jira_api_url || !settings.jira_api_token || !settings.jira_username}
                    startIcon={testingConnection ? <CircularProgress size={20} /> : null}
                  >
                    {testingConnection ? 'Testando conexão...' : 'Testar Conexão'}
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={testingConnection}
                  >
                    Salvar Configurações
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Projetos Disponíveis
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={loadProjects}
                    startIcon={loadingProjects ? <CircularProgress size={20} /> : <RefreshIcon />}
                  >
                    {loadingProjects ? 'Carregando...' : 'Atualizar Projetos'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={testConnection}
                    disabled={testingConnection || !settings.jira_api_url || !settings.jira_api_token || !settings.jira_username}
                    startIcon={testingConnection ? <CircularProgress size={20} /> : null}
                  >
                    {testingConnection ? 'Testando conexão...' : 'Testar Conexão'}
                  </Button>
                </Box>
              </Grid>

              {loadingProjects ? (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto', p: 2 }}>
                      {availableProjects.length > 0 ? (
                        <List>
                          {availableProjects.map((project) => (
                            <ListItem key={project.key} dense button onClick={() => handleProjectToggle(project.key)}>
                              <Checkbox
                                edge="start"
                                checked={selectedProjects.includes(project.key)}
                                tabIndex={-1}
                                disableRipple
                              />
                              <ListItemText 
                                primary={project.name} 
                                secondary={`Chave: ${project.key}`}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                          <Typography color="text.secondary">
                            Nenhum projeto disponível. Verifique as configurações da API do JIRA.
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        Buscar Issue
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                          fullWidth
                          label="Número da Ficha"
                          value={issueNumber}
                          onChange={(e) => setIssueNumber(e.target.value)}
                          disabled={searching}
                        />
                        <Button
                          variant="contained"
                          onClick={searchIssue}
                          disabled={searching || !issueNumber.trim() || selectedProjects.length === 0}
                          startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                        >
                          Buscar
                        </Button>
                      </Box>

                      {searchResults.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Resultados:
                          </Typography>
                          <List>
                            {searchResults.map((issue) => (
                              <ListItem key={issue.id}>
                                <ListItemText
                                  primary={issue.fields.summary}
                                  secondary={`${issue.key} - Status: ${issue.fields.status.name}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loadingProjects}
                  >
                    Salvar Configurações
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Settings; 